import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, CheckCircle } from "@phosphor-icons/react";
import { trackConversion, getAttribution } from "../../lib/analytics";
import client from "../../lib/api";

// Free Assessment lead form — the primary conversion surface on the landing page.
// Posts to /api/contact with source='free_assessment' and fires a conversion event.
export default function FreeAssessmentSection() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [fleetSize, setFleetSize] = useState("");
  const [challenge, setChallenge] = useState("");
  const [contactPref, setContactPref] = useState("email");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error("Please confirm the consent checkbox to continue.");
      return;
    }
    setSubmitting(true);
    try {
      await client.post("/contact", {
        source: "free_assessment",
        company_name: company,
        contact_name: `${firstName} ${lastName}`.trim(),
        phone,
        email,
        fleet_size: fleetSize,
        current_challenges: challenge,
        services_of_interest: `Preferred contact: ${contactPref}`,
        ...getAttribution(),
      });
      trackConversion("free_assessment_submit", { fleet_size: fleetSize });
      setSubmitted(true);
      toast.success("Assessment request received — we'll be in touch.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="assessment"
      data-testid="assessment-section"
      className="border-b border-white/10 py-20 sm:py-28 scroll-mt-20"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-16">
          <div className="lg:col-span-2">
            <div className="overline mb-3 text-[#FF9764]">Free assessment</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight mb-6">
              Get your free trucking AI{" "}
              <span className="text-[#FF5A1F]">workflow assessment</span>.
            </h2>
            <p className="text-neutral-400 text-sm sm:text-base leading-relaxed mb-6">
              A short conversation, and a written summary of where practical AI automation could
              save your team the most time. No obligation.
            </p>
            <div className="text-sm text-neutral-300 mb-2 font-medium">What we'll review:</div>
            <ul className="space-y-2 text-sm text-neutral-400">
              {[
                "Dispatch workflow",
                "Broker and customer communication",
                "Document processing",
                "Administrative tasks",
                "Invoicing and follow-up",
                "Automation opportunities",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <CheckCircle size={16} weight="fill" className="text-[#FF5A1F] mt-0.5 shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            {submitted ? (
              <div
                data-testid="assessment-success"
                className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-8 sm:p-10 text-center"
              >
                <div className="w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mx-auto mb-5">
                  <CheckCircle size={28} weight="fill" className="text-emerald-400" />
                </div>
                <h3 className="font-display text-2xl font-semibold mb-3">Assessment request received</h3>
                <p className="text-neutral-300 text-sm leading-relaxed max-w-md mx-auto">
                  Thanks — we've got your details. Our team is available 24/7 — someone from
                  FreightPilotAI will reach out within a few hours to schedule your workflow review.
                </p>
                <div className="text-xs text-neutral-500 mt-6">
                  Questions? Email us at <a href="mailto:contact@freightpilotai.ai" className="text-[#FF5A1F] hover:text-[#FF7038]">contact@freightpilotai.ai</a>
                </div>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-testid="assessment-form"
                className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 sm:p-8 space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField label="First name" required>
                    <input
                      required
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      data-testid="assessment-first-name"
                      className="w-full bg-transparent border border-white/10 rounded-md px-4 py-3 text-sm focus:border-[#FF5A1F]/60 focus:outline-none transition-colors"
                      placeholder="Alex"
                    />
                  </FormField>
                  <FormField label="Last name" required>
                    <input
                      required
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      data-testid="assessment-last-name"
                      className="w-full bg-transparent border border-white/10 rounded-md px-4 py-3 text-sm focus:border-[#FF5A1F]/60 focus:outline-none transition-colors"
                      placeholder="Rivera"
                    />
                  </FormField>
                </div>
                <FormField label="Company name" required>
                  <input
                    required
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    data-testid="assessment-company"
                    className="w-full bg-transparent border border-white/10 rounded-md px-4 py-3 text-sm focus:border-[#FF5A1F]/60 focus:outline-none transition-colors"
                    placeholder="Rivera Logistics"
                  />
                </FormField>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField label="Work email" required>
                    <input
                      required
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      data-testid="assessment-email"
                      className="w-full bg-transparent border border-white/10 rounded-md px-4 py-3 text-sm focus:border-[#FF5A1F]/60 focus:outline-none transition-colors"
                      placeholder="alex@fleet.com"
                    />
                  </FormField>
                  <FormField label="Phone" required>
                    <input
                      required
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      data-testid="assessment-phone"
                      className="w-full bg-transparent border border-white/10 rounded-md px-4 py-3 text-sm focus:border-[#FF5A1F]/60 focus:outline-none transition-colors"
                      placeholder="(555) 123-4567"
                    />
                  </FormField>
                </div>
                <FormField label="Fleet size" required>
                  <select
                    required
                    value={fleetSize}
                    onChange={(e) => setFleetSize(e.target.value)}
                    data-testid="assessment-fleet-size"
                    className="w-full bg-[#141414] border border-white/10 rounded-md px-4 py-3 text-sm focus:border-[#FF5A1F]/60 focus:outline-none transition-colors"
                  >
                    <option value="">Select fleet size…</option>
                    <option value="1-4">1 – 4 trucks</option>
                    <option value="5-24">5 – 24 trucks</option>
                    <option value="25-99">25 – 99 trucks</option>
                    <option value="100-249">100 – 249 trucks</option>
                    <option value="250-500">250 – 500 trucks</option>
                    <option value="500+">500+ trucks</option>
                  </select>
                </FormField>
                <FormField label="Primary operational challenge">
                  <textarea
                    value={challenge}
                    onChange={(e) => setChallenge(e.target.value)}
                    data-testid="assessment-challenge"
                    rows={3}
                    className="w-full bg-transparent border border-white/10 rounded-md px-4 py-3 text-sm focus:border-[#FF5A1F]/60 focus:outline-none transition-colors resize-none"
                    placeholder="Which repetitive task is costing your team the most time right now?"
                  />
                </FormField>
                <FormField label="Preferred contact method" required>
                  <div className="flex flex-wrap gap-3 pt-1">
                    {[
                      { v: "email", l: "Email" },
                      { v: "phone", l: "Phone" },
                      { v: "either", l: "Either" },
                    ].map((c) => (
                      <label
                        key={c.v}
                        className={`px-4 py-2 rounded-full border text-sm cursor-pointer transition-colors ${
                          contactPref === c.v
                            ? "border-[#FF5A1F] bg-[#FF5A1F]/10 text-white"
                            : "border-white/15 hover:border-white/30 text-neutral-300"
                        }`}
                      >
                        <input
                          type="radio"
                          name="contactPref"
                          value={c.v}
                          checked={contactPref === c.v}
                          onChange={() => setContactPref(c.v)}
                          data-testid={`assessment-pref-${c.v}`}
                          className="sr-only"
                        />
                        {c.l}
                      </label>
                    ))}
                  </div>
                </FormField>

                <label className="flex items-start gap-3 text-xs text-neutral-400 leading-relaxed pt-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    data-testid="assessment-consent"
                    className="mt-1 accent-[#FF5A1F] shrink-0"
                  />
                  <span>
                    I agree to be contacted by FreightPilotAI about my request. I understand my
                    information will be handled per the{" "}
                    <Link to="/privacy" className="text-[#FF5A1F] hover:text-[#FF7038]">Privacy Policy</Link>.
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="assessment-submit"
                  className="btn-primary rounded-full w-full px-6 py-3.5 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? "Sending…" : "Request My Free Assessment"}
                  <ArrowRight size={16} weight="bold" />
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function FormField({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">
        {label}
        {required && <span className="text-[#FF5A1F] ml-1">*</span>}
      </div>
      {children}
    </label>
  );
}
